import React from 'react'
import { useTranslation } from 'react-i18next'
import { PriceMarkerTooltip } from '../price-marker-tooltip/PriceMarkerTooltip'
import { PriceBenchmarkValueLabels } from './price-benchmark-value-labels/PriceBenchmarkValueLabels'
import { usePriceBenchmarkData } from './usePriceBenchmarkData'
import type { PriceBenchmarkSource } from './usePriceBenchmarkData'
import styles from './PriceBenchmark.module.css'

type PriceBenchmarkProps = {
  price?: number
}

const RANGE_PADDING_RATIO = 0.1
const MIN_RANGE_POSITION = RANGE_PADDING_RATIO * 100
const MAX_RANGE_POSITION = 100 - RANGE_PADDING_RATIO * 100
const MIN_BAR_POSITION = 0
const MAX_BAR_POSITION = 100

const clampToBar = (position: number) => Math.min(MAX_BAR_POSITION, Math.max(MIN_BAR_POSITION, position))

const getRangeExtension = (boundaryValue: number, range: number) => {
  const boundaryExtension = Math.abs(boundaryValue) * RANGE_PADDING_RATIO

  return boundaryExtension > 0 ? boundaryExtension : range * RANGE_PADDING_RATIO
}

const getMarkerPosition = (minValue: number, maxValue: number, markerValue: number) => {
  const range = maxValue - minValue
  if (range <= 0) {
    return 50
  }

  const rangeWidth = 100 - RANGE_PADDING_RATIO * 2 * 100
  const position = RANGE_PADDING_RATIO * 100 + ((markerValue - minValue) / range) * rangeWidth

  return clampToBar(position)
}

const getPriceTooltipPosition = (minValue: number, maxValue: number, price: number) => {
  const range = maxValue - minValue
  if (range <= 0) {
    return 50
  }

  if (price < minValue) {
    const minExtension = getRangeExtension(minValue, range)
    const position = ((price - (minValue - minExtension)) / minExtension) * MIN_RANGE_POSITION

    return clampToBar(position)
  }
  if (price > maxValue) {
    const maxExtension = getRangeExtension(maxValue, range)
    const position = MAX_RANGE_POSITION + ((price - maxValue) / maxExtension) * (MAX_BAR_POSITION - MAX_RANGE_POSITION)

    return clampToBar(position)
  }
  return getMarkerPosition(minValue, maxValue, price)
}

const getLegendTranslationKey = (source: PriceBenchmarkSource) =>
  source === 'rentIndex'
    ? 'wizard.screen.basicInfo.price.recommendedNotification.check.rentIndex'
    : 'wizard.screen.basicInfo.price.recommendedNotification.check.askingRent'

export function PriceBenchmark({ price }: PriceBenchmarkProps) {
  const { t } = useTranslation()
  const { benchmarkSource, benchmarkMinValue, benchmarkMaxValue, benchmarkRecommendedValue } = usePriceBenchmarkData()

  if (benchmarkMinValue === undefined || benchmarkMaxValue === undefined || benchmarkRecommendedValue === undefined) {
    return null
  }

  const recommendedMarkerPosition = getMarkerPosition(benchmarkMinValue, benchmarkMaxValue, benchmarkRecommendedValue)
  const priceTooltipPosition =
    price === undefined ? undefined : getPriceTooltipPosition(benchmarkMinValue, benchmarkMaxValue, price)

  return (
    <>
      <div className={`${styles.benchmark} ${priceTooltipPosition === undefined ? '' : styles.benchmarkWithTooltip}`}>
        <div className={styles.barContainer}>
          <div className={styles.bar} aria-hidden='true'>
            <div className={styles.barPadding} />
            <div className={styles.barRange} />
            <div className={styles.barPadding} />
          </div>
          <div
            className={styles.marker}
            style={{ left: `${recommendedMarkerPosition}%` }}
            data-testid='rent-price-check-marker'
          />
          {priceTooltipPosition !== undefined && (
            <PriceMarkerTooltip position={priceTooltipPosition}>
              {t('wizard.screen.basicInfo.price.recommendedNotification.check.yourPrice')}
            </PriceMarkerTooltip>
          )}
        </div>
        <PriceBenchmarkValueLabels
          minValue={benchmarkMinValue}
          maxValue={benchmarkMaxValue}
          recommendedValue={benchmarkRecommendedValue}
          minPosition={MIN_RANGE_POSITION}
          maxPosition={MAX_RANGE_POSITION}
          recommendedPosition={recommendedMarkerPosition}
          source={benchmarkSource}
        />
      </div>

      <div className={styles.legend}>
        <span className={styles.legendDot} aria-hidden='true' />
        <span className='font-body-small-regular'>{t(getLegendTranslationKey(benchmarkSource))}</span>
      </div>
    </>
  )
}

export const PriceBenchmarkTestExports = {
  getMarkerPosition,
  getPriceTooltipPosition,
  getLegendTranslationKey,
  MIN_RANGE_POSITION,
  MAX_RANGE_POSITION,
}
